import React, { useState, useEffect } from 'react';
import { Card } from '@mui/material';
import PrimaryButton from '../common/PrimaryButton';

// Helper function to convert seconds to HH:MM:SS format
const convertSecondsToHHMMSS = (seconds) => {
    const hours = Math.floor(seconds / 3600); // 1 hour = 3600 seconds
    const minutes = Math.floor((seconds % 3600) / 60); // 1 minute = 60 seconds
    const remainingSeconds = seconds % 60;

    // Format to ensure two digits for minutes and seconds
    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};


const formatTimestampToDateString = (utcTimestampInMillis) => {
    const date = new Date(utcTimestampInMillis); // Convert milliseconds to Date object
    const now = new Date();

    // Determine if the date is today, tomorrow, or a different day
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString() === date.toDateString();

    // Format time as hh:mm am/pm
    const options = { hour: "numeric", minute: "numeric", hour12: true };
    const timeString = date.toLocaleTimeString(undefined, options);

    if (isToday) {
        return `Today at ${timeString}`;
    } else if (isTomorrow) {
        return `Tomorrow at ${timeString}`;
    } else {
        // For other days, include the day and date
        const dayOptions = { weekday: "long", month: "short", day: "numeric" };
        const dayString = date.toLocaleDateString(undefined, dayOptions);
        return `${dayString} at ${timeString}`;
    }
};

// ProductCard component
const ProductCard = ({ product, handleAddToCart, productDetailNavigation, loading }) => {
    const [timeLeft, setTimeLeft] = useState(product.data.timeLeft);

    useEffect(() => {
        // Update the timer every second if the product has a flash sale
        if (product.data.isFlashSale === "Y") {
            const interval = setInterval(() => {
                setTimeLeft((prevTimeLeft) => (prevTimeLeft > 0 ? prevTimeLeft - 1 : 0));
            }, 1000);

            return () => clearInterval(interval); // Cleanup the interval on component unmount
        }
    }, [product]);

    return (
        <Card
            key={product.data.productId}
            className="flex-shrink-0 w-48 flex flex-col justify-between"
            style={{ position: 'relative', height: "310px",  background: "linear-gradient(to bottom, #fff 0%, rgba(255, 152, 0, 0.2 ) 100%)", boxShadow: "0px 2px 4px #681517", borderRadius: "10px" }} // Ensures card occupies full height
        >
            <div onClick={() => productDetailNavigation(product)} className="relative">
                <img
                    style={{ height: "150px", objectFit: "contain" }}
                    src={product.data.productImageUrl ? product.data.productImageUrl[0] : ""}
                    alt={product.data.productTitle}
                    className="w-full h-40 object-cover rounded-lg"
                />
                {product.tag && (
                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        {product.tag}
                    </div>
                )}
            </div>
            <div style={{ padding: "10px" }}>
                <h3
                    className="text-sm font-semibold text-left"
                    style={{
                        fontSize: '14px',
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        color: "#681517",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitLineClamp: 2, // Limit to 2 lines
                        lineHeight: "1.5rem", // Adjust based on your font size for alignment
                    }}
                >
                    {product.data.productTitle}
                </h3>
                <div className="flex items-center justify-center gap-2 my-1">
                    <span style={{ fontSize: '14px' }} className="text-sm text-gray-500 line-through">
                        ₹{product.data.actualPrice}
                    </span>
                    <span style={{ fontSize: '14px', color: '#681517' }} className="text-sm font-bold">₹{product.data.finalPrice}</span>
                    <span style={{ fontSize: '14px', color: '#FF9800' }} className="text-sm text-green-600">{product.data.saveText}</span>
                </div>
            </div>
            <div
                className="mt-auto p-2"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* <button style={{ position: 'absolute', bottom: '5px', fontSize: '16px', border: '#00000000', borderRadius: '20px', fontWeight: '500' }}
                    className="w-full py-2 text-white bg-green-500 font-medium hover:bg-green-600"
                    onClick={() => handleAddToCart(product.data.productId)}
                >
                    Add to Cart
                </button> */}
                <PrimaryButton label={"Add to Cart"} onClick={() =>  handleAddToCart(product.data.productId)}
                sx={{ position: 'absolute', bottom: '10px', width: '70%',  }}
                />
            </div>
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(0,0,0,0.1)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            width: "50px",
                            height: "50px",
                            border: "4px solid #f3f3f3",
                            borderRadius: "50%",
                            borderTop: "4px solid #4CAF50",
                            animation: "spin 1s linear infinite",
                        }}
                    ></div>

                    <style>
                        {`
                        @keyframes spin {
                                0% {
                                transform: rotate(0deg);
                                }
                                100% {
                                transform: rotate(360deg);
                                }
                            }
                        `}
                    </style>
                </div>
            )}

            {product.data.itemType === "upcoming-flash-sale" || product.data.isFlashSale === "Y" ? (
                <div
                    style={{
                        fontSize: '12px',
                        color: "white",
                        background: "#681517",
                        position: "absolute",
                        top: "0",
                        left: "0", // Align to the top-right corner
                        padding: "3px 6px",
                        borderBottomLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        whiteSpace: "nowrap", // Ensure the text doesn't break into multiple lines
                    }}
                >
                    {product.data.itemType === "upcoming-flash-sale"
                        ? formatTimestampToDateString(product.data.flashSaleStartTime * 1000)
                        : product.data.isFlashSale === "Y"
                            ? `Ends in: ${convertSecondsToHHMMSS(timeLeft)}`
                            : ""}
                </div>
            ) : null}
        </Card>
    );
};

export default ProductCard;
